import { AutoFixHigh, Clear, Done } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import type ICertificateDTO from '@sharedInterfaces/ICertificate';
import type { TCertificateRequestingState } from '@sharedInterfaces/ICertificate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { ICertificateMetadataResponse } from '@sharedInterfaces/websocket/ICertificateMetadata';
import createCertificate from '@src/APIs/graphQl/Certificate/createCertificate';
import editCertificate from '@src/APIs/graphQl/Certificate/editCertificate';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import Certificate from '@src/Objects/Certificate';
import { updateCertificate } from '@store/reducer/certificatesReducer';
import { setCompanyAllCertificates } from '@store/reducer/companyReducer';
import { setEmployeeCertificates } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './CertificateDialog.css';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import LinkInput from '@src/App/Components/General/FormularElements/formsControls/inputs/LinkInput/LinkInput';
import MultiTextChipSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/MultiTextChipSelect/MultiTextChipSelect';
import NumberInput from '@src/App/Components/General/FormularElements/formsControls/inputs/NumberInput/NumberInput';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import ErrorBox from '../../Error/ErrorBox/ErrorBox';
import Row from '../../General/FormularElements/Row/Row';
import RowElement from '../../General/FormularElements/RowElement/RowElement';

interface CertificateDialogProps {
    id: string;
    certificate?: Certificate;
    resolve?: (val: Certificate | null) => void;
}

/**
 * React component representing a certificate dialog.
 *
 * @param props - The props for the certificate dialog.
 * @returns JSX element representing the certificate dialog.
 */
export function CertificateDialog({ id, certificate, resolve }: CertificateDialogProps) {
    const showErrorDialog = useErrorDialog();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;

    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);
    const emp = useSelector((state: AppState) => state.employee);

    const [errorText, setErrorText] = React.useState('' as string);
    const [saving, setSaving] = React.useState(false as boolean);

    const [certificateName, setCertificateName] = React.useState(certificate ? certificate.title : ('' as string));
    const [certificateNameSuggestion, setCertificateNameSuggestion] = React.useState<string>('');
    const [certificateNameError, setCertificateNameError] = React.useState<string | undefined>(undefined);

    const [description, setDescription] = React.useState(certificate ? certificate.description : ('' as string));
    const [otherNames, setOtherNames] = React.useState<string[]>(certificate ? certificate.otherNames : []);

    const [productName, setProductName] = React.useState<string>(
        certificate && certificate.productInfo ? certificate.productInfo.productName : '',
    );
    const [manufacturer, setManufacturer] = React.useState<string>(
        certificate && certificate.productInfo ? certificate.productInfo.manufacturer : '',
    );
    const [link, setLink] = React.useState<string>(
        certificate && certificate.productInfo && certificate.productInfo.link ? certificate.productInfo.link : '',
    );
    const [prerequisites, setPrerequisites] = React.useState<string>(certificate ? certificate.prerequisites : '');

    const [durationInDays, setDurationInDays] = React.useState<number>(
        certificate?.durationInDays ? certificate.durationInDays : 0,
    );
    const [cost, setCost] = React.useState<number>(certificate?.cost ? certificate.cost : 0);
    const [acquisition, setAcquisition] = React.useState<string>(certificate ? certificate.acquisition : '');
    const [acquisitionLink, setAcquisitionLink] = React.useState<string>(
        certificate && certificate.acquisitionLink ? certificate.acquisitionLink : '',
    );

    const [generatingMetadata, setGeneratingMetadata] = React.useState<boolean>(false);

    const [subKey, setSubKey] = React.useState<string>('');
    const topic = EWebsocketTopics.generateCertificateMetadata;
    const startAnalyse = () => {
        const request: TCertificateRequestingState = {
            all: true,
        };
        WebSocketClient.sendMessage(topic, {
            certificateName,
            key: subKey,
            request,
        });
        setGeneratingMetadata(true);
    };
    React.useEffect(() => {
        const subKey = WebSocketClient.subscribe<ICertificateMetadataResponse>(
            topic,
            (data: ICertificateMetadataResponse) => {
                if (subKey !== data.key) return;
                if (data.error) {
                    showErrorDialog(new Error(data.error));
                    setGeneratingMetadata(false);
                    return;
                }
                const metaData = data.metadata;

                if (!metaData) {
                    showErrorDialog(new Error(langStrings.error));
                    setGeneratingMetadata(false);
                    return;
                }
                setGeneratingMetadata(false);
                if (metaData.description && (!description || description === '')) setDescription(metaData.description);
                if (metaData.synonyms && (!otherNames || otherNames.length === 0))
                    setOtherNames(metaData.synonyms.map((n: string) => n.toLowerCase() as string) as string[]);
                if (metaData.productInfo && (!productName || productName === ''))
                    setProductName(metaData.productInfo.productName);
                if (metaData.productInfo && (!manufacturer || manufacturer === ''))
                    setManufacturer(metaData.productInfo.manufacturer);
                if (metaData.productInfo && (!link || link === '')) setLink(metaData.productInfo.link);
                if (metaData.prerequisites && (!prerequisites || prerequisites === ''))
                    setPrerequisites(metaData.prerequisites);
                if (metaData.durationInDays && (!durationInDays || durationInDays === 0))
                    setDurationInDays(metaData.durationInDays);
                if (metaData.cost && (!cost || cost === 0)) setCost(metaData.cost);

                if (metaData.certificateName && certificateName !== metaData.certificateName) {
                    setCertificateNameSuggestion(metaData.certificateName);
                }
            },
        );
        setSubKey(subKey);
        return () => {
            return WebSocketClient.unsubscribe(subKey);
        };
    }, []);

    const onFetchError = (ex: { toString(): string }) => {
        const error = ex.toString();
        setErrorText(error);
        setTimeout(() => {
            setSaving(false);
        }, 3000);
    };
    const onFetch = (newCertificate: ICertificateDTO) => {
        if (!emp) return;
        const newEntity = new Certificate(newCertificate);
        const newAllCerts = [...allCertificates];
        const newArray = [...emp.certificates];

        if (certificate) {
            // CREATE
            let index = newAllCerts.findIndex((e) => newEntity.id === e.id);
            newAllCerts[index] = { ...newAllCerts[index], title: newEntity.title };
            index = newArray.findIndex((e) => newEntity.id === e.id);
            if (index > -1) newArray[index] = newEntity;
        } else {
            newAllCerts.push({
                id: newCertificate.id,
                title: newCertificate.title,
                otherNames,
                version: 0,
            });
            // newArray.push(newCertificate);
        }
        dispatch(setEmployeeCertificates(newArray));
        dispatch(setCompanyAllCertificates(newAllCerts));
        dispatch(updateCertificate(newEntity));
        resolve && resolve(newEntity);
        setSaving(false);
    };

    const onSave = async () => {
        setSaving(true);
        if (certificateName === '') {
            setErrorText(langStrings.newCertificateError);
            setTimeout(() => {
                setSaving(false);
            }, 1500);
            return;
        }
        if (certificate) {
            await editCertificate(
                certificate.id,
                certificateName,
                description,
                otherNames,
                productName,
                manufacturer,
                link,
                prerequisites,
                cost,
                durationInDays,
                acquisition,
                acquisitionLink,
            )
                .then(onFetch)
                .catch(onFetchError);
        } else {
            await createCertificate(
                certificateName,
                description,
                otherNames,
                productName,
                manufacturer,
                link,
                prerequisites,
                cost,
                durationInDays,
                acquisition,
                acquisitionLink,
            )
                .then(onFetch)
                .catch(onFetchError);
        }
    };
    const onClose = () => {
        resolve && resolve(null);
    };

    const existingCertificates = allCertificates
        .filter((a) => !certificate || certificate.id !== a.id)
        .map((s) => s.title.toLowerCase());

    const validate = () => {
        if (certificateName === '') {
            setCertificateNameError(langStrings.pleaseEnterTitle);
        } else if (existingCertificates.includes(certificateName.toLowerCase())) {
            setCertificateNameError(
                langStrings.errorDuplicateTitle.replace('[TITLE]', certificateName).replace('[TITLE]', certificateName),
            );
        } else {
            setCertificateNameError(undefined);
        }
    };

    useEffect(validate, [certificateName]);

    return (
        <Dialog
            id={id}
            title={certificate ? certificate.title : langStrings.newCertificate}
            onClose={onClose}
            footer={
                <div style={{ float: 'right' }}>
                    <Button
                        icon={<Done />}
                        disabled={saving}
                        text={certificate ? langStrings.save : langStrings.create}
                        size={'normal'}
                        onClick={onSave}
                    />
                </div>
            }
        >
            <div className="certificateDialog">
                <div className="newCertificate">
                    <Row>
                        <RowElement title={langStrings.certificateName} alignTitle="left">
                            <TextInput
                                value={certificateName}
                                isAI
                                onChange={setCertificateName}
                                helperText={certificateNameError}
                            />
                        </RowElement>
                        <IconButton
                            title={langStrings.generateCertificateData}
                            disabled={!!certificateNameError || generatingMetadata}
                            onClick={startAnalyse}
                        >
                            {generatingMetadata ? <CircularProgress size={24} color="secondary" /> : <AutoFixHigh />}
                        </IconButton>
                    </Row>
                    {certificateNameSuggestion !== '' && (
                        <div>
                            {langStrings.suggestedCertificateName}: {certificateNameSuggestion}
                            <IconButton
                                onClick={() => {
                                    setCertificateName(certificateNameSuggestion);
                                    setCertificateNameSuggestion('');
                                }}
                            >
                                <Done />
                            </IconButton>
                            <IconButton
                                onClick={() => {
                                    setCertificateNameSuggestion('');
                                }}
                            >
                                <Clear />
                            </IconButton>
                        </div>
                    )}
                    <Row align="top">
                        <RowElement title={langStrings.productName} alignTitle="left">
                            <TextInput size="small" value={productName} onChange={setProductName} />
                        </RowElement>
                        <RowElement title={langStrings.productInfo} alignTitle="left">
                            <LinkInput size="small" value={link} onChange={setLink} />
                        </RowElement>
                    </Row>
                    <RowElement title={langStrings.manufacturer} alignTitle="left">
                        <TextInput size="small" value={manufacturer} onChange={setManufacturer} />
                    </RowElement>
                    <Row>
                        <RowElement title={langStrings.durationInDays} alignTitle="left">
                            <NumberInput value={durationInDays} onChange={setDurationInDays} />
                        </RowElement>
                        <RowElement title={langStrings.oftenUsed.cost} alignTitle="left">
                            <NumberInput value={cost} onChange={setCost} />
                        </RowElement>
                    </Row>
                    <RowElement title={langStrings.description} alignTitle="left">
                        <FormatedTextInput value={description} onChange={setDescription} />
                    </RowElement>
                    <RowElement title={langStrings.otherNames} alignTitle="left">
                        <MultiTextChipSelect
                            value={otherNames}
                            onChange={(names) => {
                                setErrorText('');
                                const newOtherNames = Array.from(new Set(names.map((n) => n.toLowerCase()))).filter(
                                    (newON) => {
                                        const existingSkill =
                                            allCertificates.find(
                                                (a) =>
                                                    a.title.toLocaleLowerCase() === newON &&
                                                    (!certificate || a.id !== certificate.id),
                                            ) ||
                                            allCertificates.find(
                                                (a) =>
                                                    a.otherNames.find((o) => o === newON) !== undefined &&
                                                    (!certificate || a.id !== certificate.id),
                                            );
                                        if (existingSkill) {
                                            const errorTitle = existingSkill.title.toLocaleLowerCase() === newON;
                                            const errorString = (
                                                errorTitle
                                                    ? langStrings.errorDuplicateTitle
                                                    : langStrings.errorDuplicateOtherName
                                            )
                                                .replace('[TITLE]', existingSkill.title)
                                                .replace('[TITLE]', existingSkill.title)
                                                .replace('[VALUE]', newON);
                                            setErrorText(errorString);
                                        }
                                        return !existingSkill;
                                    },
                                );
                                setOtherNames(newOtherNames);
                            }}
                        />
                    </RowElement>
                    <RowElement title={langStrings.prerequisites} alignTitle="left">
                        <FormatedTextInput value={prerequisites} onChange={setPrerequisites} />
                    </RowElement>
                    <RowElement title={langStrings.oftenUsed.acquisition} alignTitle="left">
                        <FormatedTextInput value={acquisition} onChange={setAcquisition} />
                    </RowElement>
                    <RowElement title={langStrings.oftenUsed.howToLearn} alignTitle="left">
                        <LinkInput size="small" value={acquisitionLink} onChange={setAcquisitionLink} />
                    </RowElement>
                </div>
                {errorText && <ErrorBox close={() => setErrorText('')}> {errorText}</ErrorBox>}
            </div>
        </Dialog>
    );
}
